import { Injectable, inject } from '@angular/core';
import { LatLng, LatLngBounds } from '../../models/latlng';
import { BehaviorSubject, Observable, Observer, Subject } from 'rxjs';
import { UtilsService } from '../utils/utils.service';
import { MobileResolutionService } from '../platform/mobile-resolution.service';
import { AccountService } from '../account/account.service';
import { LocationService } from '../platform/location.service';
import { WebviewService } from '../platform/webview.service';
import { MapSkin, SettingsService } from '../account/settings.service';
import { ConsoleLoggerService } from '../logging/console-logger.service';
import { MapElementType } from '../../models/map-element-type';
import { Route, RoutePlanElement, RoutePlanType, Turn } from '../../models/route';
import { LanguageService } from '../account/language.service';
import { ChargingOperator } from '../../models/charging-operator';
import { SearchedRouteElement } from '../../models/searched-route-element';
import { TripPoint } from '../../models/trip';
import { ChargerProvider, ChargingGroup, ChargingStation, ChargingStationDetails, Plug } from '../../models/charging-station';
import * as CryptoJS from 'crypto-js';
import * as pako from 'pako';
import { HttpService } from '../core/http.service';
import { FollowModeState } from 'src/app/models/follow-mode-states';
import { EaseToOptions, Map } from 'maplibre-gl';
import { VehicleService } from '../vehicle/vehicle.service';

/**
 * How many decoded charging-station tiles to keep in memory.
 *
 * A tile holds every station and group in a ~1.4° cell, so the parsed form is
 * far larger than the compressed payload. Without a ceiling the cache grew by
 * one entry per tile ever visited and was never pruned, which on a long session
 * is one of the larger contributors to heap growth. The visible bounding box
 * spans only a handful of tiles, so this is generous enough that eviction only
 * ever touches regions the user panned away from long ago; anything evicted is
 * simply refetched.
 */
const MAX_CACHED_CHARGING_TILES = 64;

export const enum ShowOnMapEnum {
  BackSettings = "backSettings",
  BackHistory = "backHistory",
  BackVehicleOptions = "backVehicleOptions"
}

@Injectable({
  providedIn: 'root'
})
export class MapService {

  private map?: Map;

  private lastCoordinates: LatLng | null = null;
  private SearchedLocation: SearchedRouteElement | null = null;
  public ObservableSearchedLocation: BehaviorSubject<SearchedRouteElement | null> = new BehaviorSubject<SearchedRouteElement | null>(null);
  public ObservableSearchedMapElement: BehaviorSubject<{ elementType: MapElementType, mapElement: any } | null> = new BehaviorSubject<{ elementType: MapElementType, mapElement: any } | null>(null);
  private SelectedCoordinate: LatLng | null = null;
  public ObservableSelectedCoordinate: BehaviorSubject<LatLng | null> = new BehaviorSubject<LatLng | null>(null);

  private LastRouteData: Route | null = null;
  public ObservableLastRouteData: BehaviorSubject<Route | null> = new BehaviorSubject<Route | null>(null);

  public ObservableDrawTripToMap: BehaviorSubject<TripPoint[] | null> = new BehaviorSubject<TripPoint[] | null>(null);
  private MapBounds: LatLngBounds | null = null;
  public ObservableMoveTo: BehaviorSubject<{ latlng: LatLng, zoom: number } | null> = new BehaviorSubject<{ latlng: LatLng, zoom: number } | null>(null);
  public ObservableCursorColor: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  public ObservablePositionMapToRoute: BehaviorSubject<LatLng[] | null> = new BehaviorSubject<LatLng[] | null>(null);
  public ObservableShowOnMap: BehaviorSubject<{ type: MapElementType | ShowOnMapEnum, data?: any } | null> = new BehaviorSubject<{ type: MapElementType | ShowOnMapEnum, data?: any } | null>(null);
  public ObservableSetFollowModeRange: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  public readonly chargingFiltersChanged$ = new Subject<void>();
  private tileChargingStations: { [key: string]: ChargingStation[] } = {};
  private tileChargingGroups: { [key: string]: { [zoomlevel: string]: ChargingGroup[] } } = {};
  /** Cached tile ids, least recently used first. See MAX_CACHED_CHARGING_TILES. */
  private tileCacheOrder: string[] = [];
  public chargingOperatorsArray: ChargingOperator[] = [];
  private chargingStationVersion: string = "";
  public ObservableActivePlanningPanelRouteElement: BehaviorSubject<{ type: string, index?: number } | null> = new BehaviorSubject<{ type: string, index?: number } | null>(null);

  private readonly httpService = inject(HttpService);
  private readonly utilsService = inject(UtilsService);
  public readonly webviewService = inject(WebviewService);
  public readonly mobileResolutionService = inject(MobileResolutionService);
  public readonly accountService = inject(AccountService);
  private readonly locationService = inject(LocationService);
  private readonly settingsService = inject(SettingsService);
  private readonly consoleLoggerService = inject(ConsoleLoggerService);
  private readonly languageService = inject(LanguageService);
  public readonly vehicleService = inject(VehicleService);

  constructor() {

    if (!this.webviewService.IsGPSTWebview()) {
      this.locationService.startWatchPosition();
    }
    else {
      this.locationService.initWebViewLocation();
    }

  }

  setMapInstance(map: Map) {
    this.map = map;
  }

  getMapInstance(): Map | undefined {
    return this.map;
  }

  /**
   * Shows/hides the basemap POI layers (`poi_*` in evlight.json / evdark.json)
   * according to Settings -> POIs. setStyle() resets layout properties, so this
   * has to run again after every skin change.
   */
  public applyPoiVisibility(): void {
    const map = this.map;
    if (!map || !map.style) {
      return;
    }

    // Not gated on isStyleLoaded(): that reports false while any tile, sprite
    // image or just-updated GeoJSON source is still in flight, which is the
    // normal state when the settings dialog closes.
    const visibility = this.settingsService.getMap().POIs ? 'visible' : 'none';
    const poiLayerIds = map.getLayersOrder().filter(layerId => layerId.includes('poi_'));

    if (poiLayerIds.length === 0) {
      // Style not parsed yet (first paint, or mid skin change) - retry when it is.
      map.once('style.load', () => this.applyPoiVisibility());
      return;
    }

    for (const layerId of poiLayerIds) {
      map.setLayoutProperty(layerId, 'visibility', visibility);
    }
  }

  initLongPress(
    longPressCallback: (event: { latitude: number; longitude: number }) => void
  ): () => void {
    let longPress = false;
    let pressTimeout: any;
    let startXY: [number, number] | null = null;

    const mapCanvas = this.map!.getCanvas();

    const getLngLatFromPoint = (x: number, y: number) => {
      const rect = mapCanvas.getBoundingClientRect();
      return this.map!.unproject([x - rect.left, y - rect.top]);
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0) return; // left click / primary touch only

      longPress = true;
      startXY = [event.clientX, event.clientY];

      clearTimeout(pressTimeout);
      pressTimeout = setTimeout(() => {
        if (!longPress || !startXY) return;

        event.preventDefault();

        const lngLat = getLngLatFromPoint(startXY[0], startXY[1]);
        longPressCallback({
          latitude: lngLat.lat,
          longitude: lngLat.lng
        });
      }, 600);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!startXY) return;

      const dx = Math.abs(event.clientX - startXY[0]);
      const dy = Math.abs(event.clientY - startXY[1]);

      if (dx + dy > 30) {
        longPress = false;
        clearTimeout(pressTimeout);
      }
    };

    const cancelPress = () => {
      longPress = false;
      clearTimeout(pressTimeout);
    };

    // Pointer events (covers touch + mouse)
    mapCanvas.addEventListener('pointerdown', onPointerDown);
    mapCanvas.addEventListener('pointermove', onPointerMove);
    mapCanvas.addEventListener('pointerup', cancelPress);
    mapCanvas.addEventListener('pointercancel', cancelPress);
    mapCanvas.addEventListener('pointerleave', cancelPress);

    // Cleanup
    return () => {
      mapCanvas.removeEventListener('pointerdown', onPointerDown);
      mapCanvas.removeEventListener('pointermove', onPointerMove);
      mapCanvas.removeEventListener('pointerup', cancelPress);
      mapCanvas.removeEventListener('pointercancel', cancelPress);
      mapCanvas.removeEventListener('pointerleave', cancelPress);
    };
  }

  public animateMapTo(
    lon?: number,
    lat?: number,
    bearing?: number,
    pitch?: number,
    zoom?: number,
    offset?: [number, number],
    duration?: number
  ) {
    const options: EaseToOptions = {};

    if (lat !== undefined && lon !== undefined) {
      options.center = [lon, lat]; // ✅ correct order

      if (offset === undefined) {
        // Push the cursor below the map center so more road ahead is visible,
        // but keep it clear of the bottom nav-plan panel (was 0.3, which sat
        // the cursor behind the panel).
        const yOffset = window.innerHeight * 0.2;
        options.offset = [0, yOffset];
      }
      else {
        options.offset = offset;
      }
    }

    if (bearing !== undefined) options.bearing = bearing;
    if (pitch !== undefined) options.pitch = pitch;
    if (zoom !== undefined) options.zoom = zoom;

    options.duration = duration !== undefined ? duration : 400;
    options.easing = (t) => t; // linear

    this.map!.easeTo(options);
  }

  centerMap(mapZoom: number, lastCursorLat: number | null, lastCursorLon: number | null) {
    if (this.map && lastCursorLat !== null && lastCursorLon !== null) {
      this.map.flyTo({
        center: [lastCursorLon, lastCursorLat],
        zoom: mapZoom,
        essential: true // This ensures the animation is not interrupted
      });
    }
  }

  getMapBounds(): { west: number; south: number; east: number; north: number } {
    return {
      west: this.map!.getBounds().getWest(),
      south: this.map!.getBounds().getSouth(),
      east: this.map!.getBounds().getEast(),
      north: this.map!.getBounds().getNorth()
    };
  }

  followModeStateChange(currentState: FollowModeState, mapPitch: number): FollowModeState {
    if ((currentState === FollowModeState.PANNED || currentState === FollowModeState.TWO_D_NORTH) && mapPitch < 10) {
      return FollowModeState.TWO_D_FOLLOW;
    }
    if ((currentState === FollowModeState.PANNED || currentState === FollowModeState.TWO_D_NORTH) && mapPitch >= 10) {
      return FollowModeState.THREE_D_FOLLOW;
    }
    if (currentState === FollowModeState.TWO_D_FOLLOW) {
      return FollowModeState.THREE_D_FOLLOW;
    }
    if (currentState === FollowModeState.THREE_D_FOLLOW) {
      return FollowModeState.TWO_D_NORTH;
    }
    return currentState;
  }

  /*fitRouteToMap(route: RoutePoint[]): void {
    if (!this.map || !route || route.length === 0) {
      return;
    }

    // Initialize bounds
    const bounds = new LngLatBounds();

    // Extend bounds with each route point
    for (const point of route) {
      bounds.extend([point.lon, point.lat]);
    }

    // Fit map to bounds
    this.map.fitBounds(bounds, {
      padding: {
        top: 250,
        bottom: 200,
        left: 60,
        right: 100
      },
      duration: 600,   // smooth animation (ms)
      bearing: 0,
      maxZoom: 17      // prevent zooming in too far
    });
  }*/

  public setOperatorsArray(chargingOperatorsArray: ChargingOperator[]): void {
    this.chargingOperatorsArray = chargingOperatorsArray;
  }

  public setLastCoordinates(lat: number, lng: number): void {
    this.lastCoordinates = new LatLng(lat, lng);
  }

  public getLastCoordinates(): LatLng | null {
    return this.lastCoordinates;
  }

  public getChargingOperatorByID(id: number): ChargingOperator | undefined {
    return this.chargingOperatorsArray.find((item) => {
      return item.id == id;
    });
  }

  public setSearchedLocation(searchedEl: SearchedRouteElement): void {
    this.SearchedLocation = searchedEl;
    this.ObservableSearchedLocation.next(searchedEl);
  }

  public setSearchedMapElement(elementType: MapElementType, mapElement: any): void {
    this.ObservableSearchedMapElement.next({ elementType: elementType, mapElement: mapElement });
  }

  public getSearchedLocation(): SearchedRouteElement | null {
    return this.SearchedLocation;
  }

  public setSelectedCoordinate(latlon: LatLng): void {
    if (latlon != undefined && this.map) {
      this.map.flyTo({
        center: [latlon.lng, latlon.lat],
        zoom: 18,
        essential: true
      });
    }
  }

  public setMoveTo(latlon: LatLng): void {
    this.map?.flyTo({
      center: [latlon.lng, latlon.lat],
      zoom: 12,
      essential: true
    });
  }

  public getSelectedCoordinate(): LatLng | null {
    return this.SelectedCoordinate;
  }

  public getLastRouteData(): Route | null {
    return this.LastRouteData;
  }

  public setLastRouteData(params: Route) {
    this.LastRouteData = params;
    this.ObservableLastRouteData.next(params);
  }

  /*public getMapBounds(): LatLngBounds {
    return this.MapBounds;
  }*/

  public setMapBounds(bounds: LatLngBounds): void {
    this.MapBounds = bounds;
  }

  public getReverseGeocode(lat: number, lng: number): Observable<string> {
    return new Observable((observer: Observer<string>) => {
      this.httpService.getReverseGeocode(lat, lng).subscribe(
        {
          next: (resp: any) => {
            observer.next(this.parseAddressString(resp, lat, lng));
            observer.complete();
          },
          error: (error) => {
            observer.next(error);
            observer.complete();
          }
        });
    });
  }

  public parseAddressString(addressObj: any, lat: number, lon: number): string {
    let address = "";
    if (addressObj.street) {
      address += addressObj.street + " ";
    }
    if (addressObj.street && addressObj.housenumber) {
      address += addressObj.housenumber + ", ";
    }
    if (addressObj.city) {
      address += addressObj.city + ", ";
    }
    if (addressObj.county) {
      address += addressObj.county + ", ";
    }
    if (addressObj.country) {
      address += addressObj.country;
    }
    if (addressObj.country == "#country_") {
      address = Math.round(lat * 1000) / 1000 + " " + Math.round(lon * 1000) / 1000;
    }

    return address;
  }

  // generating arrow layer for a lod level using element indexes
  /*public generateTrackArrows(latlonArray: TripPoint[], indexes: number, lodLevelMultiply: number): L.Marker[] {
    const markersArray = [];
    const arrowsArray = [];
    // define arrows between points
    for (let i = 1; i < indexes.length; i++) {
      const currElement = latlonArray[indexes[i]];
      const prevElement = latlonArray[indexes[i - 1]];
      if (prevElement.lat != currElement.lat || prevElement.lng != currElement.lng) {

        const arrowArrayLatLon = new L.LatLng((currElement.lat + prevElement.lat) / 2, (currElement.lng + prevElement.lng) / 2);
        const arrowArrayRotation = 180 + this.utilsService.angleFromCoordinate(prevElement.lat, prevElement.lng, currElement.lat, currElement.lng);
        arrowsArray.push({ latlon: arrowArrayLatLon, rotation: arrowArrayRotation });
      }
    }
    // keep arrows, with a defined distance between each other
    let lastPoint = null;
    for (let j = 0; j < arrowsArray.length; j++) {
      const currentPoint = arrowsArray[j].latlon;
      if (j == 0 || (lastPoint.distanceTo(currentPoint) > lodLevelMultiply)) {
        const arrowIcon: L.DivIcon = L.divIcon({
          className: 'arrow-icon',
          html: '<img src="assets/icons/default/map/direction_arrow.png" style="width: 12px; height: 12px; transform:rotate(' + arrowsArray[j].rotation + 'deg);" />',
          iconSize: null,
          iconAnchor: [6, 6]
        });
        markersArray.push(L.marker(arrowsArray[j].latlon, { icon: arrowIcon, interactive: false }));
        lastPoint = currentPoint;
      }
    }
    return markersArray;
  }*/

  public getRoutePlanByType(routePlanList: RoutePlanElement[], type: RoutePlanType): RoutePlanElement[] {
    return routePlanList.filter((routePlanEl) => { return routePlanEl.Type == type });
  }

  public getRoutePlanStartPoint(routePlanList: RoutePlanElement[]): RoutePlanElement | undefined {
    return routePlanList.find((routePlanEl) => { return routePlanEl.Type == RoutePlanType.StartPoint });
  }

  public getRoutePlanEndPoint(routePlanList: RoutePlanElement[]): RoutePlanElement | undefined {
    return routePlanList.find((routePlanEl) => { return routePlanEl.Type == RoutePlanType.EndPoint || routePlanEl.Type == RoutePlanType.ChargerEndPoint });
  }

  public getSingleAndMultipleChargingStations(routePlanList: RoutePlanElement[]): { SingleChargingStations: RoutePlanElement[], MultiChargingStations: RoutePlanElement[][] } {
    const resp: { SingleChargingStations: RoutePlanElement[], MultiChargingStations: RoutePlanElement[][] } = { SingleChargingStations: [], MultiChargingStations: [] };
    const chargingStations: RoutePlanElement[] = [... this.getRoutePlanByType(routePlanList, RoutePlanType.Charger) as RoutePlanElement[]];

    // collect same location charging stations
    let duplicates = chargingStations.filter((item, index) => {
      return chargingStations.map((ChargingStationElement) => { return ChargingStationElement.ChargingStationId }).indexOf(item.ChargingStationId) !== index;
    });

    // collect multiple stop charging stations
    for (let i = 0; i < duplicates.length; i++) {
      let multipleChargingStationElement: RoutePlanElement[] = [];
      for (let j = 0; j < chargingStations.length; j++) {
        if (duplicates[i].ChargingStationId == chargingStations[j].ChargingStationId) {
          multipleChargingStationElement.push(chargingStations[j])
        }
      }
      resp.MultiChargingStations.push(multipleChargingStationElement);
    }

    // remove multiple stop charging stations from the original array
    for (let i = 0; i < duplicates.length; i++) {
      for (let j = chargingStations.length - 1; j >= 0; j--) {
        if (duplicates[i].ChargingStationId == chargingStations[j].ChargingStationId) {
          chargingStations.splice(j, 1);
        }
      }
    }

    resp.SingleChargingStations = chargingStations;

    return resp;
  };

  public initTurnIconAndInstruction(turns: Turn[]): Turn[] {
    let waypointIdx = 0;
    turns.forEach((turn, index) => {
      if (turn.TypeIndex < 12 || turn.TypeIndex >= 93) {
        if (turn.TypeIndex == 95) {
          turn.Instruction[0] = this.utilsService.turnList[turn.TypeIndex].name;
          turn.Icon[0] = `exit_roundabout${turn.RoundaboutExitNumber}.svg`;
        } else {
          turn.Instruction[0] = this.utilsService.turnList[turn.TypeIndex].name;
          turn.Icon[0] = this.utilsService.turnList[turn.TypeIndex].icon;
        }
        // left sided roundabouts
        if (turn.IsLeftHandTraffic && (turn.RoundaboutExitNumber == 2 || turn.TypeIndex == 93 || turn.TypeIndex == 95)) {
          turn.Icon[0] = turn.Icon[0].substring(0, turn.Icon[0].length - 4) + "_eng.svg";
        }
        // waypoint indices
        if (turn.TypeIndex == 103) {
          waypointIdx++;
          turn.Icon[0] = turn.Icon[0].substring(0, turn.Icon[0].length - 4) + waypointIdx + ".svg";
        }
      }
      else {
        /// COMBINED TURNS
        const actTurn = { ... this.utilsService.turnList[turn.TypeIndex] };
        const icon = actTurn.id.toLowerCase().split("_and_");
        const combinedName = actTurn.name.toLowerCase().split(` ${this.languageService.languageJSON.Global_And.toLowerCase()} `);
        turn.Instruction[0] = combinedName[0] + ` ${this.languageService.languageJSON.Global_And.toLowerCase()}`;
        turn.Instruction[1] = combinedName[1];
        turn.Icon[0] = icon[0] + ".svg";
        turn.Icon[1] = icon[1] + ".svg";
      }
    });

    return turns;
  }

  public setChargingStationVersion(version: string): void {
    this.chargingStationVersion = version;
  }

  public getChargingStationVerions(): string {
    return this.chargingStationVersion;
  }

  public getTileChargingStations(): { [key: string]: ChargingStation[] } {
    return this.tileChargingStations;
  }

  public getTileChargingGroups(): { [key: string]: { [zoomlevel: string]: ChargingGroup[] } } {
    return this.tileChargingGroups;
  }

  public hasTileDownloaded(tileX: number, tileY: number): boolean {
    const tileId = this.getTileId(tileX, tileY);
    const downloaded = this.tileChargingStations.hasOwnProperty(tileId);
    if (downloaded) {
      // Called for every tile in the visible bounds on each bounding-box
      // refresh, which makes it the natural place to keep the cache ordered by
      // actual use rather than by download time.
      this.touchTile(tileId);
    }
    return downloaded;
  }

  /**
   * Move `tileId` to the most-recently-used end of the cache, then evict from
   * the other end until the cache is back within MAX_CACHED_CHARGING_TILES.
   */
  private touchTile(tileId: string): void {
    const position = this.tileCacheOrder.indexOf(tileId);
    if (position !== -1) {
      this.tileCacheOrder.splice(position, 1);
    }
    this.tileCacheOrder.push(tileId);

    while (this.tileCacheOrder.length > MAX_CACHED_CHARGING_TILES) {
      const evicted = this.tileCacheOrder.shift()!;
      delete this.tileChargingStations[evicted];
      delete this.tileChargingGroups[evicted];
    }
  }

  public getChargingStationTile(tileX: number, tileY: number): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      const tileId = this.getTileId(tileX, tileY);
      const typeFlag = this.getTypeFlag();

      function xzPlb9EUTgTzPT0sOuSf1LXr9iAPQsJ(bStr, jk) {
        const fibSeq = "WQ48EJxAMUAQRAM5GEovngvEyDW3LqN";
        let rslt = '', lpIdx = jk ? 16 : 32;
        while (rslt.length < 16) {
          const cIdx = fibSeq.charCodeAt(lpIdx++ % fibSeq.length) % bStr.length;
          rslt += bStr.charAt(cIdx);
        }
        return CryptoJS.enc.Utf8.parse(rslt);
      }

      const errorStr = 'The server has detected a temporal anomaly in the request timestamp that conflicts with the current server time. This may occur due to discrepancies in client-server synchronization or due to unintentional time travel attempts.';
      const hash = CryptoJS.SHA256(tileId + '.json' + errorStr).toString(CryptoJS.enc.Hex);

      this.httpService.getChargingTile(typeFlag, tileId, hash, this.chargingStationVersion).subscribe((encryptedData: ArrayBuffer) => {
        try {
          const wordArray = CryptoJS.lib.WordArray.create(new Uint8Array(encryptedData));
          const decrypted = CryptoJS.AES.decrypt({ ciphertext: wordArray }, xzPlb9EUTgTzPT0sOuSf1LXr9iAPQsJ(errorStr, true), { iv: xzPlb9EUTgTzPT0sOuSf1LXr9iAPQsJ(errorStr, false) });
          const binaryDataLength = decrypted.sigBytes;
          const binaryData = new Uint8Array(binaryDataLength);
          for (let i = 0; i < binaryDataLength; i += 4) {
            const word = decrypted.words[Math.floor(i / 4)];
            binaryData[i] = (word >>> 24) & 0xFF;
            binaryData[i + 1] = (word >>> 16) & 0xFF;
            binaryData[i + 2] = (word >>> 8) & 0xFF;
            binaryData[i + 3] = word & 0xFF;
          }
          const decompressed = pako.inflate(binaryData, { to: 'string' });
          const resp = JSON.parse(decompressed);

          this.tileChargingStations[tileId] = this.parseChargingStationsTileArray(resp.chargingstations);
          this.tileChargingGroups[tileId] = this.parseChargingGroupTileArray(resp.groups);
          this.touchTile(tileId);

          observer.next(true);
          observer.complete();
        } catch (error) {
          console.error('Error processing data', error);
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  public getTileId(tileX: number, tileY: number): string {
    const normTiles = this.getNormTiles(tileX, tileY);
    const typeFlag = this.getTypeFlag();

    return `${normTiles}_${typeFlag}`;
  }

  private getNormTiles(tileX: number, tileY: number): string {
    const tileXNorm = tileX.toString().padStart(3, '0');
    const tileYNorm = tileY.toString().padStart(2, '0');

    return `${tileXNorm}_${tileYNorm}`;
  }

  public getTypeFlag(): string {
    let flag = 0;
    for (let type of this.vehicleService.getSelectedCar().ChargerTypes) {
      flag |= (1 << (type - 1));
    }

    return flag.toString().padStart(2, '0');
  }

  private parseChargingStationsTileArray(tileArray): ChargingStation[] {
    const chargingStationArray: ChargingStation[] = [];

    for (let i = 0; i < tileArray.length; i++) {
      if (tileArray[i][5][0][1] > 1) {
        let types = "";
        let maxpower: number = 0;
        for (let j = 0; j < tileArray[i][5].length; j++) {
          if (tileArray[i][5][j][1] > maxpower) {
            maxpower = tileArray[i][5][j][1];
          }
          types += tileArray[i][5][j][0].toString();
        }

        let img = "";
        if (maxpower > 35) {
          img = "cs_big3";
        }
        else if (maxpower > 10) {
          img = "cs_big2";
        }
        else {
          img = "cs_big1";
        }
        if (this.settingsService.getMap().Skin === MapSkin.Light) {
          img += "_light"
        }

        const charger: ChargingStation = {
          id: tileArray[i][0].toLowerCase(),
          lat: tileArray[i][2],
          lon: tileArray[i][3],
          minZoomLevel: tileArray[i][4],
          plugs: tileArray[i][5].map((el) => {
            const plug: Plug = { type: el[0], power: el[1] }
            return plug;
          }),
          img: img,
          types: types,
          maxpower: maxpower,
          opid: tileArray[i][1]
        };

        chargingStationArray.push(charger);
      }
    }

    return chargingStationArray;
  }

  private parseChargingGroupTileArray(tileArray): { [key: string]: ChargingGroup[] } {
    const groupTileArray = {};
    tileArray.forEach(zoomLevelArray => {
      groupTileArray[zoomLevelArray[0]] = zoomLevelArray[1].map((chargingGroup: any) => {
        const group: ChargingGroup = {
          lat: chargingGroup[0],
          lon: chargingGroup[1],
          maxpower: chargingGroup[2],
          powerid: this.getPowerId(chargingGroup[2]),
          count: chargingGroup[3]
        }
        return group;
      });
    });

    return groupTileArray;
  }

  private getPowerId(chargePower) {
    if (chargePower > 35) {
      return 3;
    }
    else if (chargePower > 10) {
      return 2;
    }
    else {
      return 1;
    }
  }

  public getChargingStationDetail(chargingStationIds: string[]): Observable<ChargingStationDetails[] | null> {
    return new Observable((observer: Observer<ChargingStationDetails[] | null>) => {

      this.httpService.getChargingStationDetails(chargingStationIds).subscribe({
        next: (resp: any) => {
          const chargingStationDetails = resp.map((el, index) => {

            const pattern = /^[A-F0-9]{8}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{12}$/;
            let provider;
            /** ecomovement charger */
            if (pattern.test(chargingStationIds[index])) {
              provider = ChargerProvider.Ecomovement;
            }
            /** ocm charger */
            else {
              provider = ChargerProvider.OCM;
            }

            let img = "";
            if (el.maxpower > 35) {
              img = "cs_big3";
            }
            else if (el.maxpower > 10) {
              img = "cs_big2";
            }
            else {
              img = "cs_big1";
            }
            if (this.settingsService.getMap().Skin === MapSkin.Light) {
              img += "_light"
            }

            const csDetails: ChargingStationDetails = {
              id: el.id,
              provider: provider,
              lat: el.lat,
              lon: el.lon,
              plugs: el.plugs,
              opid: el.opid,
              img: img,
              cost: el.cost,
              email: el.email,
              phone: el.phone,
              title: el.title
            }

            if (el.carplaces?.length > 0) {
              el.plugs.forEach(plugEl => {
                plugEl.count = 0;
                plugEl.available = 0;
                plugEl.statusCount = 0;
              });

              el.carplaces.forEach((carplaceEl) => {
                const count = {};
                const limitedItems = carplaceEl.plugs.filter(item => (count[item.type] = (count[item.type] || 0) + 1) <= 1);

                limitedItems.forEach((carplacesPlugEl) => {
                  const plug = el.plugs.find((plugEl) => { return carplacesPlugEl.type == plugEl.type && carplacesPlugEl.power == plugEl.power });
                  if (carplaceEl.status && carplaceEl.status != "UNKNOWN") {
                    plug.statusCount++;
                  }
                  plug.count++;
                  if (carplaceEl.status == "AVAILABLE") {
                    plug.available++;
                  }
                })
              });
            }

            return csDetails;
          });

          observer.next(chargingStationDetails);
          observer.complete();
        },
        error: (error) => {
          observer.next(null);
          observer.complete();
        }
      })
    });
  }

  public getChargingStationListStatus(latMin: number, latMax: number, lonMin: number, lonMax: number, usedPlugTypes: number[]): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.getChargingStationStatus(latMin, latMax, lonMin, lonMax, usedPlugTypes).subscribe({
        next: (resp: any) => {
          const chargerStatusList = {};
          for (let i = 0; i < resp.length; i++) {
            chargerStatusList[resp[i][0]] = resp[i][1];
          }

          observer.next(chargerStatusList);
          observer.complete();
        },
        error: (error) => {
          observer.next(null);
          observer.complete();
        }
      })
    });
  }

  public getFavoriteChargingStationListStatus(favoriteChargingStations: string[]): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.getFavoriteChargingStationStatus(favoriteChargingStations).subscribe({
        next: (resp: any) => {
          const chargerStatusList = {};
          for (let i = 0; i < resp.length; i++) {
            chargerStatusList[resp[i][0]] = resp[i][1];
          }

          observer.next(chargerStatusList);
          observer.complete();
        },
        error: (error) => {
          observer.next(null);
          observer.complete();
        }
      })
    });
  }

  public setChargingStationRecalc(): void {
    this.chargingFiltersChanged$.next();
  }
}
